import React from 'react';
import { Button, type ButtonProps } from './ui/button';
import { Loader2, Lightbulb } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { cn } from '@/lib/utils';
import { ButtonTooltip, TooltipContent, TooltipProvider } from './ui/tooltip';

interface AnalyzeButtonProps {
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  isAnalyzing?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
  dialogPrimary?: boolean;
  tooltip?: string;
  tooltipSide?: React.ComponentProps<typeof TooltipContent>['side'];
  matchLabelWidth?: React.ReactNode;
}

export const AnalyzeButton: React.FC<AnalyzeButtonProps> = ({
  onClick,
  isAnalyzing = false,
  disabled = false,
  children,
  variant = 'inverse',
  size = 'sm',
  className,
  dialogPrimary,
  tooltip,
  tooltipSide = 'bottom',
  matchLabelWidth,
}) => {
  const { t } = useLocale();
  const label = children ?? (isAnalyzing ? t('analyzing') : t('analyze'));
  const effectiveTooltip = isAnalyzing ? t('ongoing_analysis_please_wait') : tooltip;
  const labelContent = matchLabelWidth ? (
    <span className="inline-grid justify-items-center">
      <span className="invisible col-start-1 row-start-1 whitespace-nowrap" aria-hidden>
        {matchLabelWidth}
      </span>
      <span className="col-start-1 row-start-1 whitespace-nowrap">{label}</span>
    </span>
  ) : (
    label
  );

  const button = (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      disabled={disabled || isAnalyzing}
      className={cn(
        'mr-6 h-auto min-h-8 max-w-full gap-1 whitespace-normal group/analyze-button',
        matchLabelWidth && 'justify-center',
        className
      )}
      dialogPrimary={dialogPrimary}
    >
      {isAnalyzing ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Lightbulb className="h-4 w-4 transition-transform duration-200 group-hover/analyze-button:text-[var(--scribane-green)] group-hover/analyze-button:scale-110" />
      )}
      {labelContent}
    </Button>
  );

  if (!effectiveTooltip) {
    return button;
  }

  return (
    <TooltipProvider>
      <ButtonTooltip tooltip={effectiveTooltip} side={tooltipSide}>
        {button}
      </ButtonTooltip>
    </TooltipProvider>
  );
};
